import { supabase } from '../lib/clients/supabase';

/**
 * Fetch availability for a specific date
 * @param {string} date - Date in ISO format (YYYY-MM-DD)
 * @returns {Promise<Object>} - Availability data
 */
export const fetchDateAvailability = async (date) => {
    try {
        const { data, error } = await supabase
            .from('availability')
            .select('*')
            .eq('date', date)
            .single();

        if (error) throw error;
        
        return { success: true, data };
    } catch (error) {
        return { 
            success: false, 
            error: { message: error.message || 'Failed to fetch availability' } 
        };
    }
};

/**
 * Fetch availability for a date range
 * @param {string} startDate - Start date in ISO format (YYYY-MM-DD)
 * @param {string} endDate - End date in ISO format (YYYY-MM-DD)
 * @returns {Promise<Object>} - Availability data for the date range
 */
export const fetchDateRangeAvailability = async (startDate, endDate) => {
    try {
        const { data, error } = await supabase
            .from('availability')
            .select('*')
            .gte('date', startDate)
            .lte('date', endDate);

        if (error) throw error;
        
        // Convert array to object with dates as keys for easier access
        const availabilityMap = {};
        data.forEach(item => {
            availabilityMap[item.date] = item;
        });
        
        return { success: true, data: availabilityMap };
    } catch (error) {
        return { 
            success: false, 
            error: { message: error.message || 'Failed to fetch availability range' } 
        };
    }
};

/**
 * Create or update availability for a specific date
 * @param {string} date - Date in ISO format (YYYY-MM-DD)
 * @param {boolean} available - Whether the date is available
 * @param {Object} timeSlots - Object with time slots and their availability
 * @returns {Promise<Object>} - Updated availability data
 */
export const updateDateAvailability = async (date, available, timeSlots) => {
    try {
        // Check if the date already exists
        const { data: existingData } = await supabase
            .from('availability')
            .select('id')
            .eq('date', date)
            .single();

        let result;
        
        if (existingData) {
            // Update existing record
            result = await supabase
                .from('availability')
                .update({
                    available,
                    time_slots: timeSlots,
                    updated_at: new Date()
                })
                .eq('id', existingData.id)
                .select()
                .single();
        } else {
            // Create new record
            result = await supabase
                .from('availability')
                .insert({
                    date,
                    available,
                    time_slots: timeSlots
                })
                .select()
                .single();
        }

        if (result.error) throw result.error;
        
        return { success: true, data: result.data };
    } catch (error) {
        return { 
            success: false, 
            error: { message: error.message || 'Failed to update availability' } 
        };
    }
};

/**
 * Update availability for a date range (bulk update)
 * @param {Array} availabilityData - Array of availability objects
 * @returns {Promise<Object>} - Result of the operation
 */
export const bulkUpdateAvailability = async (availabilityData) => {
    try {
        const { data, error } = await supabase
            .from('availability')
            .upsert(availabilityData, { 
                onConflict: 'date',
                returning: 'minimal'
            });

        if (error) throw error;
        
        return { success: true, data };
    } catch (error) {
        return { 
            success: false, 
            error: { message: error.message || 'Failed to update availability' } 
        };
    }
};

/**
 * Fetch the recurring schedule
 * @returns {Promise<Object>} - Recurring schedule data
 */
export const fetchRecurringSchedule = async () => {
    try {
        // First, check if any record exists
        const { data: existingData, error: checkError } = await supabase
            .from('recurring_schedule')
            .select('id')
            .order('created_at', { ascending: false })
            .limit(1);

        if (checkError) throw checkError;

        let result;
        
        if (!existingData || existingData.length === 0) {
            // If no record exists, create one with empty schedule
            result = await supabase
                .from('recurring_schedule')
                .insert({ schedule: {} })
                .select('schedule')
                .single();
        } else {
            // If record exists, fetch it
            result = await supabase
                .from('recurring_schedule')
                .select('schedule')
                .order('created_at', { ascending: false })
                .limit(1)
                .single();
        }

        if (result.error) throw result.error;
        
        return { success: true, data: result.data?.schedule || {} };
    } catch (error) {
        return { 
            success: false, 
            error: { message: error.message || 'Failed to fetch recurring schedule' } 
        };
    }
};

/**
 * Update the recurring schedule
 * @param {string} weekday - The day of the week to update
 * @param {string} timeSlot - The time slot to update
 * @param {boolean} isAvailable - Whether the time slot is available
 * @returns {Promise<Object>} - Updated schedule data
 */
export const updateRecurringSchedule = async (weekday, timeSlot, isAvailable) => {
    try {
        // First, get the current schedule
        const { data: currentData, error: fetchError } = await supabase
            .from('recurring_schedule')
            .select('id, schedule')
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

        if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

        let result;
        // Initialize an empty schedule object
        let updatedSchedule = {};
        
        if (currentData?.schedule) {
            try {
                // Try to parse if it's a string, otherwise use as is
                updatedSchedule = typeof currentData.schedule === 'string' 
                    ? JSON.parse(currentData.schedule) 
                    : currentData.schedule;
                
                // Ensure it's an object
                if (typeof updatedSchedule !== 'object' || updatedSchedule === null) {
                    updatedSchedule = {};
                }
            } catch (parseError) {
                // If parsing fails, start with a fresh object
                console.warn('Failed to parse schedule, starting fresh:', parseError);
                updatedSchedule = {};
            }
        }

        // Update the specific time slot
        if (!updatedSchedule[weekday]) {
            updatedSchedule[weekday] = {};
        }
        updatedSchedule[weekday][timeSlot] = isAvailable;
        
        if (!currentData) {
            // If no record exists, create one
            result = await supabase
                .from('recurring_schedule')
                .insert({ schedule: updatedSchedule })
                .select('schedule')
                .single();
        } else {
            // If record exists, update it
            result = await supabase
                .from('recurring_schedule')
                .update({ schedule: updatedSchedule })
                .eq('id', currentData.id)
                .select('schedule')
                .single();
        }

        if (result.error) throw result.error;
        
        // Ensure we return a valid object
        let returnedSchedule = {};
        try {
            returnedSchedule = typeof result.data?.schedule === 'string'
                ? JSON.parse(result.data.schedule)
                : result.data?.schedule || {};
                
            // Ensure it's an object
            if (typeof returnedSchedule !== 'object' || returnedSchedule === null) {
                returnedSchedule = {};
            }
        } catch (parseError) {
            console.warn('Failed to parse returned schedule:', parseError);
            returnedSchedule = {};
        }
        
        return { success: true, data: returnedSchedule };
    } catch (error) {
        return { 
            success: false, 
            error: { message: error.message || 'Failed to update recurring schedule' } 
        };
    }
};

/**
 * Update availability after a booking is made
 * @param {string} date - Date in ISO format (YYYY-MM-DD)
 * @param {string} timeSlot - Time slot (e.g., "09:00")
 * @param {string} bookingId - Optional booking ID to associate with the time slot
 * @returns {Promise<Object>} - Result of the operation
 */
export const markTimeSlotBooked = async (date, timeSlot, bookingId = null) => {
    try {
        // Get current availability
        const { data: existingData, error: fetchError } = await supabase
            .from('availability')
            .select('id, time_slots')
            .eq('date', date)
            .single();

        if (fetchError && fetchError.code !== 'PGRST116') {
            throw fetchError;
        }

        let result;
        
        if (existingData) {
            // Update existing time slots
            const updatedTimeSlots = {
                ...existingData.time_slots,
                [timeSlot]: bookingId ? { available: false, booking_id: bookingId } : false
            };
            
            result = await supabase
                .from('availability')
                .update({
                    time_slots: updatedTimeSlots,
                    updated_at: new Date()
                })
                .eq('id', existingData.id)
                .select()
                .single();
        } else {
            // Create new availability record with this time slot marked as unavailable
            result = await supabase
                .from('availability')
                .insert({
                    date,
                    available: true,
                    time_slots: { 
                        [timeSlot]: bookingId ? { available: false, booking_id: bookingId } : false 
                    }
                })
                .select()
                .single();
        }

        if (result.error) throw result.error;
        
        return { success: true, data: result.data };
    } catch (error) {
        return { 
            success: false, 
            error: { message: error.message || 'Failed to update time slot' } 
        };
    }
};

/**
 * Mark a time slot as available (for cancellations or admin management)
 * @param {string} date - Date in ISO format (YYYY-MM-DD)
 * @param {string} timeSlot - Time slot (e.g., "09:00")
 * @returns {Promise<Object>} - Result of the operation
 */
export const markTimeSlotAvailable = async (date, timeSlot) => {
    try {
        // Get current availability
        const { data: existingData, error: fetchError } = await supabase
            .from('availability')
            .select('id, time_slots')
            .eq('date', date)
            .single();

        if (fetchError && fetchError.code !== 'PGRST116') {
            throw fetchError;
        }

        let result;
        
        if (existingData) {
            // Create a copy of the existing time slots
            const updatedTimeSlots = { ...existingData.time_slots };
            
            // Mark the time slot as available by setting it to true
            // or removing it from the object (both approaches work)
            updatedTimeSlots[timeSlot] = true;
            
            result = await supabase
                .from('availability')
                .update({
                    time_slots: updatedTimeSlots,
                    updated_at: new Date()
                })
                .eq('id', existingData.id)
                .select()
                .single();
        } else {
            // If no record exists, create one with all slots available
            result = await supabase
                .from('availability')
                .insert({
                    date,
                    available: true,
                    time_slots: { [timeSlot]: true }
                })
                .select()
                .single();
        }

        if (result.error) throw result.error;
        
        return { success: true, data: result.data };
    } catch (error) {
        return { 
            success: false, 
            error: { message: error.message || 'Failed to update time slot' } 
        };
    }
};

/**
 * Check if a specific date and time slot is available
 * @param {string} date - Date in ISO format (YYYY-MM-DD)
 * @param {string} timeSlot - Time slot (e.g., "09:00")
 * @returns {Promise<Object>} - Availability status
 */
export const checkTimeSlotAvailability = async (date, timeSlot) => {
    try {
        // Get the day of the week (0-6, where 0 is Sunday)
        const dayOfWeek = new Date(date).getDay();
        const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const weekday = weekdays[dayOfWeek];

        // 1. Check availability table for specific date override
        const { data: dateData, error: dateError } = await supabase
            .from('availability')
            .select('available, time_slots')
            .eq('date', date)
            .maybeSingle();

        if (dateError) {
            throw dateError;
        }

        // If there's a specific override for this date
        if (dateData) {
            // If the date is explicitly marked as unavailable, return false
            if (dateData.available === false) {
                return { success: true, available: false };
            }

            // Check if there's a specific override for this time slot
            const timeSlotData = dateData.time_slots?.[timeSlot];
            if (timeSlotData !== undefined) {
                const isAvailable = typeof timeSlotData === 'boolean' ? timeSlotData : timeSlotData.available !== false;
                return { success: true, available: isAvailable };
            }
        }

        // 2. If no specific override exists, check recurring_schedule
        const { data: recurringData, error: recurringError } = await supabase
            .from('recurring_schedule')
            .select('schedule')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

        if (recurringError) {
            throw recurringError;
        }

        // Parse the recurring schedule if it's a string
        let recurringSchedule = {};
        try {
            recurringSchedule = typeof recurringData?.schedule === 'string'
                ? JSON.parse(recurringData.schedule)
                : recurringData?.schedule || {};
        } catch (parseError) {
            console.warn('Failed to parse recurring schedule:', parseError);
        }

        // Check if there's a recurring schedule for this weekday and time slot
        if (recurringSchedule[weekday]?.[timeSlot] !== undefined) {
            return { 
                success: true, 
                available: recurringSchedule[weekday][timeSlot] 
            };
        }

        // 3. If no schedule exists, default to available
        return { success: true, available: true };
    } catch (error) {
        return { 
            success: false, 
            error: { message: error.message || 'Failed to check availability' } 
        };
    }
};

/**
 * Get available time slots for a specific date
 * @param {string} date - Date in ISO format (YYYY-MM-DD)
 * @returns {Promise<Object>} - Available time slots
 */
export const getAvailableTimeSlots = async (date) => {
    try {
        // Get the day of the week
        const dayOfWeek = new Date(date).getDay();
        const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const weekday = weekdays[dayOfWeek];

        // 1. Get specific date availability
        const { data: dateData, error: dateError } = await supabase
            .from('availability')
            .select('available, time_slots')
            .eq('date', date)
            .maybeSingle();

        if (dateError) throw dateError;

        // 2. Get recurring schedule
        const { data: recurringData, error: recurringError } = await supabase
            .from('recurring_schedule')
            .select('schedule')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

        if (recurringError) throw recurringError;

        // Parse recurring schedule
        let recurringSchedule = {};
        try {
            recurringSchedule = typeof recurringData?.schedule === 'string'
                ? JSON.parse(recurringData.schedule)
                : recurringData?.schedule || {};
        } catch (parseError) {
            console.warn('Failed to parse recurring schedule:', parseError);
        }

        // If date is explicitly marked as unavailable, return empty array
        if (dateData?.available === false) {
            return { success: true, data: [] };
        }

        // Get all time slots from recurring schedule for this weekday
        const recurringTimeSlots = recurringSchedule[weekday] || {};

        // If there's a specific date override, merge it with recurring schedule
        const timeSlots = { ...recurringTimeSlots };
        if (dateData?.time_slots) {
            Object.assign(timeSlots, dateData.time_slots);
        }

        // Convert to array format
        const availableSlots = Object.entries(timeSlots).map(([time, available]) => ({
            time,
            available: typeof available === 'boolean' ? available : available?.available !== false
        }));

        return { success: true, data: availableSlots };
    } catch (error) {
        return { 
            success: false, 
            error: { message: error.message || 'Failed to get available time slots' } 
        };
    }
};
